import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskLines, EASE } from "./motion";
import { IMAGES, CONTACT } from "../../content/site";
import { scrollToId } from "./useLenis";
import { WindLeaves } from "./WindLeaves";
import { useIntroReady } from "./Intro";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.08, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "-30%"]);
  const ready = useIntroReady();
  const go = (v) => (ready ? v : undefined);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#0D2F18]">
      <motion.div
        className="absolute inset-0"
        style={{ y: imgY, scale: imgScale }}
        initial={{ clipPath: reduce ? "inset(0% 0% 0% 0%)" : "inset(12% 10% 12% 10% round 32px)" }}
        animate={go({ clipPath: "inset(0% 0% 0% 0% round 0px)" })}
        transition={{ duration: 1.6, ease: EASE }}
      >
        <img src={IMAGES.hero} alt="Gepflegter Garten mit Rasen und Beeten" className="h-full w-full object-cover" fetchpriority="high" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D2F18]/90 via-[#0D2F18]/35 to-[#0D2F18]/30" />
      <div className="grain absolute inset-0" />
      {ready && <WindLeaves />}

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-32 sm:px-8 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={go({ opacity: 1, y: 0 })}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-[#DCEFBE]"
        >
          <span className="h-px w-10 bg-[#DCEFBE]" /> Gartenpflege · {CONTACT.area}
        </motion.p>
        <h1 data-testid="hero-heading" className="font-display text-[clamp(2.6rem,6.8vw,7.5rem)] font-medium leading-[0.95] tracking-[-0.03em] text-[#FBF9F4]">
          <MaskLines lines={["Wir bringen frischen Wind", <em key="e" className="font-normal italic text-[#DCEFBE]">in Ihren Garten.</em>]} delay={0.35} play={ready} />
        </h1>
        <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={go({ opacity: 1, y: 0 })}
            transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="max-w-md text-base leading-relaxed text-[#FBF9F4]/90 md:text-lg"
          >
            GRÜNWIRBEL kümmert sich um Rasen, Hecken und Beete – zuverlässig, sorgfältig und mit Freude an der Arbeit draußen.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={go({ opacity: 1, y: 0 })}
            transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
            className="flex flex-wrap gap-3"
          >
            <button data-testid="hero-cta-button" onClick={() => scrollToId("kontakt")} className="btn-light group">
              Unverbindlich anfragen
              <span className="grid h-8 w-8 place-items-center rounded-full bg-[#1E5631] text-[#FBF9F4] transition-transform duration-300 group-hover:rotate-[-45deg]">
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>
            <button data-testid="hero-services-button" onClick={() => scrollToId("leistungen")} className="btn-ghost-light">
              Leistungen ansehen <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
